import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { addToCart, formatPrice } from "../lib/cart";
import { getRecommendations, BUNDLE_COMPONENTS } from "../lib/recommendations";
import TopNav from "../components/TopNav";
import Logo from "../components/Logo";
import { LangToggle, useLang } from "../lib/i18n";
import { toast } from "sonner";
import { getResultsContent } from "../lib/resultsContent";

const PRODUCT_LABEL = { tirzepatide: "Tirzepatide", nad: "NAD+", sermorelin: "Sermorelin" };
const PRODUCT_IMG = {
  tirzepatide: "https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f3554514daed238d4b4c7b.png",
  nad:         "https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f3554514daed238d4b4c7c.png",
  sermorelin:  "https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f355450549a7387e14d21f.png",
};
const PROGRAM_IMG = {
  "tirzepatide-core":        "https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f3554514daed238d4b4c7b.png",
  "metabolic-boost":         "https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f355454ad535b652de4b0e.png",
  "total-body-optimization": "https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f3554514daed238d4b4c77.png",
  "advanced-transformation": "https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f35545b7fe31537a9abb9c.png",
};

// Inclusions shown inside each bundle card. Trust-building copy, not pricing.
function getBundleInclusions(lang) {
  const rc = getResultsContent(lang);
  const bi = rc.bundleInclusions;
  return {
    "tirzepatide-core":         [bi.tirzPatientProtocol, bi.medicalEval, bi.ongoingSupervision],
    "metabolic-boost":          [bi.tirzPatientProtocol, bi.nadVial, bi.medicalEval],
    "total-body-optimization":  [bi.tirzPatientProtocol, bi.nadVial, bi.sermorelin9mg, bi.medicalEval],
    "advanced-transformation":  [bi.tirzMaxDose, bi.nadVial, bi.sermorelin15mg, bi.medicalEval],
  };
}

function ResultsNav() {
  const { lang } = useLang();
  const rc = getResultsContent(lang);
  return (
    <nav className="hp-nav" data-testid="ir-nav">
      <Logo size="md" linkTo="/" testId="ir-logo"/>
      <ul className="hp-nav-links">
        <li><Link to="/">{rc.home}</Link></li>
        <li><Link to="/estore">{rc.shop}</Link></li>
      </ul>
      <div className="hp-nav-right">
        <LangToggle />
        <Link to="/cart" className="hp-cart" aria-label="Cart" data-testid="ir-nav-cart">🛒</Link>
      </div>
    </nav>
  );
}

// ─── Bundle Card ──────────────────────────────────────────────────────────
function BundleResultCard({ program, isPrimary, onAdd, catalog, bundleBilling, setBundleBilling }) {
  const { lang } = useLang();
  const rc = getResultsContent(lang);

  if (!program) return null;
  const inclusions = getBundleInclusions(lang)[program.slug] || [];
  const monthly = program.monthly_cents;
  const threeMonthTotal = monthly * 3;

  // For Tirzepatide Core, allow one-time purchase option
  const isTirzCore = program.slug === "tirzepatide-core";
  const mode = bundleBilling[program.slug] || "membership";

  // Get retail price for one-time purchase (from catalog)
  let retailPrice = null;
  if (isTirzCore && catalog) {
    const tirzProduct = catalog.products.find(p => p.slug === "tirzepatide");
    if (tirzProduct && tirzProduct.doses.length > 0) {
      retailPrice = tirzProduct.doses[0].retail_cents;
    }
  }

  const displayPrice = (isTirzCore && mode === "retail") ? retailPrice : monthly;
  const totalPrice = (isTirzCore && mode === "retail") ? retailPrice : threeMonthTotal;

  return (
    <div className="aa-pcard" data-testid={`bundle-card-${program.slug}`} style={{height:"100%"}}>
      <img src={PROGRAM_IMG[program.slug] || PRODUCT_IMG.tirzepatide} alt=""
           className="aa-pcard-img" style={{objectFit:"cover", background:"var(--dark2)"}}/>
      <div className="aa-pcard-body">
        {isPrimary && <span className="aa-badge" data-testid={`badge-bundle-${program.slug}`}>⭐ {rc.recommended}</span>}
        {!isPrimary && program.badge === "most_popular" && <span className="aa-badge">{rc.mostPopular}</span>}
        {!isPrimary && program.badge === "maximum" && <span className="aa-badge">{rc.maximum}</span>}
        <h3 className="aa-h2" style={{marginTop:8, fontSize:"clamp(1.1rem, 2vw, 1.4rem)"}}>{program.name}</h3>
        <p className="aa-card-sub" style={{marginBottom:14, fontSize:"clamp(0.8rem, 1.5vw, 0.9rem)"}}>{program.tagline}</p>

        {isTirzCore && retailPrice && (
          <>
            <label className="aa-label" style={{fontSize:"clamp(0.65rem, 1.2vw, 0.78rem)"}}>{rc.pricing}</label>
            <div className="aa-price-row" style={{marginBottom:10, gap:"8px"}}>
              <button className={`aa-price-tile ${mode==="membership"?"active":""}`}
                      onClick={()=>setBundleBilling({...bundleBilling, [program.slug]: "membership"})}
                      data-testid={`bundle-mode-${program.slug}-membership`}
                      style={{padding:"10px 8px"}}>
                <div className="aa-price-label" style={{fontSize:"clamp(0.6rem, 1vw, 0.7rem)"}}>{rc.membership}</div>
                <div className="aa-price-val" style={{fontSize:"clamp(1.1rem, 2vw, 1.6rem)"}}>{formatPrice(monthly)}<span style={{fontSize:"0.65em"}}>{rc.mo}</span></div>
              </button>
              <button className={`aa-price-tile ${mode==="retail"?"active":""}`}
                      onClick={()=>setBundleBilling({...bundleBilling, [program.slug]: "retail"})}
                      data-testid={`bundle-mode-${program.slug}-retail`}
                      style={{padding:"10px 8px"}}>
                <div className="aa-price-label" style={{fontSize:"clamp(0.6rem, 1vw, 0.7rem)"}}>{rc.oneTimeOption}</div>
                <div className="aa-price-val" style={{fontSize:"clamp(1.1rem, 2vw, 1.6rem)"}}>{formatPrice(retailPrice)}</div>
              </button>
            </div>
          </>
        )}

        {!isTirzCore && (
          <>
            <div style={{display:"flex", alignItems:"baseline", gap:10, margin:"6px 0 14px"}}>
              <div style={{fontFamily:"var(--font-display)", color:"var(--gold)", fontSize:"clamp(1.3rem, 2.5vw, 1.7rem)", fontWeight:700}}>
                {formatPrice(monthly)}<span style={{fontSize:"0.7em", letterSpacing:".06em"}}>{rc.mo}</span>
              </div>
            </div>
          </>
        )}

        {mode === "membership" && (
          <p className="aa-caption" style={{marginTop:-6, marginBottom:12, fontSize:"clamp(0.7rem, 1.3vw, 0.85rem)", lineHeight:1.4}}>
            {rc.threemonthCommitment} · {formatPrice(threeMonthTotal)} {rc.total}
          </p>
        )}
        {mode === "retail" && isTirzCore && (
          <p className="aa-caption" style={{marginTop:-6, marginBottom:12, fontSize:"clamp(0.7rem, 1.3vw, 0.85rem)"}}>
            {rc.oneTime}
          </p>
        )}

        {inclusions.length > 0 && (
          <>
            <div className="aa-label" style={{marginTop:6, fontSize:"clamp(0.65rem, 1.2vw, 0.78rem)"}}>{rc.includes}</div>
            <ul style={{margin:"6px 0 12px", paddingLeft:16, color:"var(--white)", fontSize:"clamp(0.75rem, 1.4vw, 0.88rem)", lineHeight:1.45}}>
              {inclusions.map((it, i) => (
                <li key={i} style={{marginBottom:3}}>{it}</li>
              ))}
            </ul>
          </>
        )}

        <div style={{marginTop:"auto", paddingTop:10}}>
          <button className="aa-btn" onClick={onAdd} data-testid={`add-bundle-${program.slug}`} style={{width:"100%", padding:"12px 16px", fontSize:"clamp(0.8rem, 1.5vw, 0.92rem)"}}>
            {rc.addToMyPlan}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function IntakeResults() {
  const nav = useNavigate();
  const { lang } = useLang();
  const rc = getResultsContent(lang);
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [billing, setBilling] = useState({});
  const [doseSel, setDoseSel] = useState({});
  const [bundleBilling, setBundleBilling] = useState({});

  useEffect(() => {
    api.get("/intake/results").then(r => {
      // No matching submission for this session token → it expired or the
      // user arrived on a build that doesn't have the new header-based session.
      // Force a clean restart instead of showing a misleading fallback.
      if (!r.data.found) {
        try { localStorage.removeItem("aabb_intake_session"); } catch {}
        nav("/intake/start?expired=1");
        return;
      }
      setData(r.data);
      api.get(`/catalog?state=${r.data.state_code}`).then(c => setCatalog(c.data));
    }).catch(() => {
      // Network/CORS failure — also send the user back so they don't get stuck.
      nav("/intake/start?expired=1");
    });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !catalog) return <><ResultsNav/><div className="aa-shell"><p>Loading…</p></div></>;

  const { result, answers } = data;
  // Prefer the top-level `intent` column (May-2026 schema). Fall back to the legacy
  // `answers._intent` for rows submitted before the schema bump.
  const intent = data.intent || (answers && answers._intent) || { kind: "generic" };
  const perProduct = result.products;
  const globalBlock = !!result.globalBlock;

  // Single source of truth for what to render.
  const plan = getRecommendations(intent, perProduct, globalBlock);

  // ── Global block ────────────────────────────────────────────────────────
  if (plan.mode === "global_block") {
    const calUrl = process.env.REACT_APP_GHL_CALENDAR_URL;
    return (
      <>
        <ResultsNav />
        <div className="aa-shell" data-testid="results-global-block">
          <div className="aa-eyebrow">{rc.basedOnAnswers}</div>
          <h1 className="aa-h1">{rc.bookConsultTitle}</h1>
          <p className="aa-sub">{rc.bookConsultSubtitle}</p>
          <a className="aa-btn" href={calUrl} target="_blank" rel="noreferrer" data-testid="results-book-consult">
            {rc.bookConsultButton}
          </a>
        </div>
      </>
    );
  }

  // ── Helpers for product cards (unchanged ProductCard look) ──────────────
  const addProductToCart = (slug, dose, mode) => {
    const cents = mode === "membership" ? dose.membership_cents : dose.retail_cents;
    const modeLabel = mode === "membership" ? rc.membership : rc.retail;
    addToCart({
      sku: `${slug}:${dose.id}:${mode}`,
      name: `${PRODUCT_LABEL[slug]} ${dose.label} (${modeLabel})`,
      price_cents: cents,
      slug, dose_id: dose.id, billing_mode: mode,
      kind: "individual",
    });
    toast.success(`${PRODUCT_LABEL[slug]} ${rc.addedToCart}`);
  };

  const productCard = (slug, isPrimary) => {
    const product = catalog.products.find(p => p.slug === slug);
    if (!product || product.doses.length === 0) return null;
    const selDose = doseSel[slug] || product.doses[0].id;
    const dose = product.doses.find(d => d.id === selDose) || product.doses[0];
    const mode = billing[slug] || "membership";
    const status = perProduct[slug] && perProduct[slug].status;

    return (
      <div key={slug} className="aa-pcard" data-testid={`product-card-${slug}`}>
        <img src={PRODUCT_IMG[slug] || product.image} alt="" className="aa-pcard-img"
             style={{objectFit:"cover", background:"var(--dark2)"}}/>
        <div className="aa-pcard-body">
          {isPrimary && <span className="aa-badge" data-testid={`badge-product-${slug}`}>⭐ {rc.recommended}</span>}
          {status === "FLAG" && <span className="aa-pill">{rc.physicianReview}</span>}
          <h3 className="aa-h2" style={{marginTop:8}}>{PRODUCT_LABEL[slug]}</h3>
          <p className="aa-card-sub" style={{marginBottom:14}}>{product.tagline}</p>

          {product.doses.length > 1 && (
            <>
              <label className="aa-label">{rc.dose}</label>
              <div className="aa-price-row">
                {product.doses.map(d => (
                  <button key={d.id} className={`aa-price-tile ${selDose===d.id?"active":""}`}
                          onClick={()=>setDoseSel({...doseSel, [slug]: d.id})}
                          data-testid={`dose-${slug}-${d.id}`}>
                    <div className="aa-price-label">{rc.strength}</div>
                    <div style={{fontWeight:700, fontSize:"1.1rem"}}>{d.label}</div>
                  </button>
                ))}
              </div>
            </>
          )}

          <label className="aa-label">{rc.pricing}</label>
          <div className="aa-price-row">
            <button className={`aa-price-tile ${mode==="membership"?"active":""}`}
                    onClick={()=>setBilling({...billing, [slug]: "membership"})}
                    data-testid={`mode-${slug}-membership`}>
              <div className="aa-price-label">{rc.membership}</div>
              <div className="aa-price-val">{formatPrice(dose.membership_cents)}{rc.mo}</div>
            </button>
            <button className={`aa-price-tile ${mode==="retail"?"active":""}`}
                    onClick={()=>setBilling({...billing, [slug]: "retail"})}
                    data-testid={`mode-${slug}-retail`}>
              <div className="aa-price-label">{rc.retail}</div>
              <div className="aa-price-val">{formatPrice(dose.retail_cents)}</div>
            </button>
          </div>
          {mode === "membership" && <p className="aa-caption">{rc.minimumCommitment}</p>}

          <div style={{marginTop:18}}>
            <button className="aa-btn" onClick={()=>addProductToCart(slug, dose, mode)} data-testid={`add-cart-${slug}`}>
              {rc.addToMyPlan}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Bundle add-to-cart ──────────────────────────────────────────────────
  const addBundleToCart = (program) => {
    const mode = bundleBilling[program.slug] || "membership";
    let priceCents = program.monthly_cents;

    // For Tirzepatide Core with retail mode, use the retail price from catalog
    if (program.slug === "tirzepatide-core" && mode === "retail") {
      const tirzProduct = catalog.products.find(p => p.slug === "tirzepatide");
      if (tirzProduct && tirzProduct.doses.length > 0) {
        priceCents = tirzProduct.doses[0].retail_cents;
      }
    }

    const billingLabel = mode === "membership" ? rc.membership : rc.oneTimeOption;
    addToCart({
      sku: `program:${program.slug}:${mode}`,
      name: `${program.name} (${billingLabel})`,
      price_cents: priceCents,
      slug: program.slug, billing_mode: mode,
      kind: "program",
    });
    toast.success(`${program.name} ${rc.addedToCart}`);
  };

  const lookupProgram = (slug) => (catalog.programs || []).find(p => p.slug === slug);

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <TopNav />
      <div className="aa-shell-wide">
        <div className="aa-eyebrow">{rc.personalizedForYou}</div>
        <h1 className="aa-h1">{rc.qualifyTitle}</h1>
        <p className="aa-sub">{rc.reviewSubtitle}</p>

        {plan.note && (
          <div data-testid="results-note" style={{
            background:"rgba(193,127,58,.10)", border:"1px solid rgba(193,127,58,.30)",
            color:"var(--white)", padding:"12px 16px", borderRadius:10, marginBottom:20,
            fontSize:".95rem",
          }}>{plan.note}</div>
        )}

        {/* TRACK A or TRACK A (with note) — bundles only */}
        {plan.mode === "bundles" && (
          <div className="aa-grid-bundles" data-testid="results-bundles-grid">
            {plan.bundles.map(slug => {
              const p = lookupProgram(slug);
              return p ? (
                <BundleResultCard
                  key={slug}
                  program={p}
                  isPrimary={slug === plan.primary}
                  onAdd={() => addBundleToCart(p)}
                  catalog={catalog}
                  bundleBilling={bundleBilling}
                  setBundleBilling={setBundleBilling}
                />
              ) : null;
            })}
          </div>
        )}

        {/* TRACK A fallback OR TRACK B — singles only */}
        {(plan.mode === "singles_fallback" || plan.mode === "singles_only") && (
          <div className="aa-grid" data-testid="results-singles-grid">
            {plan.singles.map(slug => productCard(slug, slug === plan.primary))}
          </div>
        )}

        {/* TRACK C — both sections */}
        {plan.mode === "generic" && (
          <>
            {plan.bundles.length > 0 && (
              <>
                <h3 style={{fontFamily:"var(--font-display)", color:"var(--gold)", margin:"10px 0 16px"}}>
                  {rc.recommendedPrograms}
                </h3>
                <div className="aa-grid-bundles" data-testid="results-bundles-grid">
                  {plan.bundles.map(slug => {
                    const p = lookupProgram(slug);
                    return p ? (
                      <BundleResultCard
                        key={slug}
                        program={p}
                        isPrimary={slug === plan.primaryBundle}
                        onAdd={() => addBundleToCart(p)}
                        catalog={catalog}
                        bundleBilling={bundleBilling}
                        setBundleBilling={setBundleBilling}
                      />
                    ) : null;
                  })}
                </div>
              </>
            )}
            {plan.singles.length > 0 && (
              <>
                <h3 style={{fontFamily:"var(--font-display)", color:"var(--gold)", margin:"36px 0 16px"}}>
                  {rc.individualTreatments}
                </h3>
                <div className="aa-grid" data-testid="results-singles-grid">
                  {plan.singles.map(slug => productCard(slug, slug === plan.primarySingle))}
                </div>
              </>
            )}
          </>
        )}

        <div style={{marginTop:40, textAlign:"center"}}>
          <Link to="/checkout" className="aa-btn" data-testid="continue-to-checkout" style={{
            display:"inline-block", padding:"14px 32px", fontSize:"1.05rem"
          }}>
            {rc.continueToCheckout}
          </Link>
        </div>

        <p className="aa-caption" style={{marginTop:30}}>{rc.pricesVary}</p>
      </div>
    </>
  );
}
